import React from 'react';

/** Offer ModelDelivery-only table layout (do not import from TE). */

export const DELIVERY_TABLE_STYLE = {
  tableLayout: 'fixed',
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
};

/**
 * Main delivery tables only. Below this width the fixed % columns get too
 * narrow (badges spill into the next cell), so the wrapper scrolls instead.
 */
export const DELIVERY_MAIN_TABLE_STYLE = {
  ...DELIVERY_TABLE_STYLE,
  minWidth: '1000px',
};

/** Fits the read-only Compliant / Countered / Not Offered badges. */
const CAPABLE_COL_STYLE = { width: '112px' };

export const CELL_WRAP_STYLE = {
  minWidth: 0,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

export const HEADER_CELL_STYLE = {
  whiteSpace: 'normal',
  lineHeight: 1.2,
  verticalAlign: 'middle',
  textAlign: 'center',
  hyphens: 'none',
  wordBreak: 'keep-all',
  overflowWrap: 'normal',
};

export const COUNTER_BADGE_STYLE = {
  backgroundColor: '#F06548',
  color: '#fff',
  fontSize: '10px',
  lineHeight: 1.15,
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  display: 'inline-block',
  padding: '2px 6px',
};

/**
 * Continuous orange marker for origin + nested counter (same left edge).
 * Do not combine with Bootstrap `.border-0` — it uses !important and kills this.
 */
export const COUNTER_ROW_MARK_STYLE = {
  borderLeft: '3px solid #F06548',
  borderTop: '0',
  borderRight: '0',
  borderBottom: '0',
  boxSizing: 'border-box',
};

/** Multi-line header label to avoid title overlap in fixed columns. */
export const DeliveryThLabel = ({ title, lines }) => (
  <span
    className='d-inline-block w-100'
    title={title || (lines || []).filter(Boolean).join(' ')}
    style={{ textAlign: 'center', lineHeight: 1.2 }}
  >
    {(lines || []).map((line, index) => (
      <React.Fragment key={`${line}-${index}`}>
        {index > 0 ? <br /> : null}
        {line}
      </React.Fragment>
    ))}
  </span>
);

export const withHeaderStyle = style => ({
  ...HEADER_CELL_STYLE,
  ...style,
});

const pct = width => ({ width, ...CELL_WRAP_STYLE });

/** NonLPG main table — widths must sum to 100%. */
export const getNonLpgColStyles = ({
  usesDyedUndyed = false,
  showCounterOfferColumn = false,
} = {}) => {
  if (usesDyedUndyed && showCounterOfferColumn) {
    return {
      expand: { width: '3%' },
      basis: { width: '6%' },
      incoterm: { width: '6%' },
      loading: { width: '6%' },
      port: pct('12%'),
      capable: CAPABLE_COL_STYLE,
      alpha: pct('16%'),
      counter: { width: '5%' },
    };
  }
  if (usesDyedUndyed) {
    return {
      expand: { width: '3%' },
      basis: { width: '6%' },
      incoterm: { width: '7%' },
      loading: { width: '7%' },
      port: pct('13%'),
      capable: CAPABLE_COL_STYLE,
      alpha: pct('16%'),
      counter: { width: '0%' },
    };
  }
  if (showCounterOfferColumn) {
    return {
      expand: { width: '3%' },
      basis: { width: '6%' },
      incoterm: { width: '8%' },
      loading: { width: '8%' },
      port: pct('15%'),
      capable: CAPABLE_COL_STYLE,
      alpha: pct('18%'),
      counter: { width: '6%' },
    };
  }
  return {
    expand: { width: '3%' },
    basis: { width: '7%' },
    incoterm: { width: '9%' },
    loading: { width: '9%' },
    port: pct('15%'),
    capable: CAPABLE_COL_STYLE,
    alpha: pct('21%'),
    counter: { width: '0%' },
  };
};

/** LPG main table column widths (%). Pricing prioritized. */
export const getLpgColStyles = ({
  requiresCargoRatio = false,
  showCounterOfferColumn = false,
} = {}) => {
  if (requiresCargoRatio && showCounterOfferColumn) {
    return {
      expand: { width: '3%' },
      incoterm: { width: '8%' },
      port: pct('10%'),
      cargoRatio: { width: '7%' },
      parcel: { width: '6%' },
      capable: CAPABLE_COL_STYLE,
      alpha: pct('18%'),
      counter: { width: '5%' },
    };
  }
  if (requiresCargoRatio) {
    return {
      expand: { width: '3%' },
      incoterm: { width: '9%' },
      port: pct('11%'),
      cargoRatio: { width: '8%' },
      parcel: { width: '7%' },
      capable: CAPABLE_COL_STYLE,
      alpha: pct('20%'),
      counter: { width: '0%' },
    };
  }
  if (showCounterOfferColumn) {
    return {
      expand: { width: '3%' },
      incoterm: { width: '9%' },
      port: pct('12%'),
      cargoRatio: { width: '0%' },
      parcel: { width: '8%' },
      capable: CAPABLE_COL_STYLE,
      alpha: pct('18%'),
      counter: { width: '5%' },
    };
  }
  return {
    expand: { width: '3%' },
    incoterm: { width: '10%' },
    port: pct('13%'),
    cargoRatio: { width: '0%' },
    parcel: { width: '9%' },
    capable: CAPABLE_COL_STYLE,
    alpha: pct('20%'),
    counter: { width: '0%' },
  };
};

/**
 * Nested NonLPG counter — no Capable in budget; widths sum to 100%.
 * Port 1 widened for location/origin content.
 */
export const getNonLpgCounterColStyles = ({
  usesDyedUndyed = false,
  showActions = false,
} = {}) => {
  if (usesDyedUndyed && showActions) {
    return {
      expand: { width: '4%' },
      basis: { width: '5%' },
      incoterm: { width: '6%' },
      loading: { width: '6%' },
      port1: pct('19%'),
      port2: pct('11%'),
      port3: pct('10%'),
      alpha: pct('15%'),
      counter: { width: '9%' },
    };
  }
  if (usesDyedUndyed) {
    return {
      expand: { width: '4%' },
      basis: { width: '6%' },
      incoterm: { width: '7%' },
      loading: { width: '7%' },
      port1: pct('22%'),
      port2: pct('12%'),
      port3: pct('10%'),
      alpha: pct('16%'),
      counter: { width: '0%' },
    };
  }
  if (showActions) {
    return {
      expand: { width: '4%' },
      basis: { width: '6%' },
      incoterm: { width: '8%' },
      loading: { width: '8%' },
      port1: pct('22%'),
      port2: pct('12%'),
      port3: pct('10%'),
      alpha: pct('20%'),
      counter: { width: '10%' },
    };
  }
  return {
    expand: { width: '4%' },
    basis: { width: '6%' },
    incoterm: { width: '9%' },
    loading: { width: '9%' },
    port1: pct('24%'),
    port2: pct('13%'),
    port3: pct('12%'),
    alpha: pct('23%'),
    counter: { width: '0%' },
  };
};

/** Nested LPG counter — Port 1 widened for origin/location content. */
export const getLpgCounterColStyles = ({
  requiresCargoRatio = false,
  showActions = false,
} = {}) => {
  if (requiresCargoRatio && showActions) {
    return {
      expand: { width: '4%' },
      incoterm: { width: '8%' },
      port1: pct('20%'),
      port2: pct('7%'),
      port3: pct('6%'),
      cargoRatio: { width: '7%' },
      parcel: { width: '6%' },
      alpha: pct('16%'),
      counter: { width: '5%' },
    };
  }
  if (requiresCargoRatio) {
    return {
      expand: { width: '4%' },
      incoterm: { width: '9%' },
      port1: pct('22%'),
      port2: pct('8%'),
      port3: pct('7%'),
      cargoRatio: { width: '8%' },
      parcel: { width: '7%' },
      alpha: pct('18%'),
      counter: { width: '0%' },
    };
  }
  if (showActions) {
    return {
      expand: { width: '4%' },
      incoterm: { width: '9%' },
      port1: pct('24%'),
      port2: pct('9%'),
      port3: pct('8%'),
      cargoRatio: { width: '0%' },
      parcel: { width: '8%' },
      alpha: pct('18%'),
      counter: { width: '6%' },
    };
  }
  return {
    expand: { width: '4%' },
    incoterm: { width: '10%' },
    port1: pct('26%'),
    port2: pct('10%'),
    port3: pct('9%'),
    cargoRatio: { width: '0%' },
    parcel: { width: '9%' },
    alpha: pct('18%'),
    counter: { width: '0%' },
  };
};
